import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { localizeUrl } from '@theme/utilities';
import {
  isAvailable,
  waitForAvailable,
  searchPage,
  landingPage,
  normalizeList,
  notify,
  cardData,
  moneyFormatter,
} from '@theme/voyado-client';

/** Backoff before the single retry a failed full grid load gets (ms). */
const RETRY_DELAY = 500;

/**
 * Renders Voyado Elevate results into the product grid, for both the search results
 * page and collection (landing) pages.
 *
 * The section server-renders skeleton cells; this component fetches Voyado results and
 * replaces them with cards cloned from the dedicated, configurable `voyado-product-card`
 * <template> and filled via `data-voyado-slot` hooks. Voyado is the only source of products
 * here — there is no server-rendered native grid behind it — so the empty state is reached
 * only after waiting for the app embed (see #start) and retrying a failed load once.
 *
 * Data attributes on <voyado-grid>:
 *   - data-mode="search" | "collection"
 *   - data-page-reference="/{handle}"  (collection mode; the Voyado page id)
 *   - data-currency="EUR"              (price formatting)
 *   - data-shop-currency="EUR"         (Voyado's price currency; mismatch ⇒ server-rendered prices)
 *
 * @extends {Component<{}>}
 */
export class VoyadoGrid extends Component {
  #abortController = new AbortController();
  /** @type {Element | null} */ #grid = null;
  /** @type {Element | null | undefined} */ #card = null;
  #limit = 24;
  #skip = 0;
  #totalHits = 0;
  #renderedCount = 0;
  #loading = false;
  /** @type {IntersectionObserver | null} */ #observer = null;
  /** @type {IntersectionObserver | null} */ #cardObserver = null;
  /** @type {Map<string, Promise<any|null>>} */ #productCache = new Map();
  /** @type {((amount: number) => string) | null} */ #money = null;
  /** @type {Array<{index: number, node: Element}> | null} */ #bannersParsed = null;
  /** @type {Map<string, {columns: string, rows: string, breakpoints: string}> | null} */ #highlightsParsed = null;

  connectedCallback() {
    super.connectedCallback();
    this.#start();
  }

  // Called by morph.js when the section re-renders in-place (e.g. theme editor settings change).
  // Without this, the new data-infinite-scroll attribute is applied but the component never restarts.
  updatedCallback() {
    super.updatedCallback();
    this.#abortController.abort();
    this.#abortController = new AbortController();
    this.#clearControls();
    this.#skip = 0;
    this.#loading = false;
    this.#totalHits = 0;
    this.#renderedCount = 0;
    this.#bannersParsed = null;
    this.#highlightsParsed = null;
    this.#productCache = new Map();
    this.#start();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#observer?.disconnect();
    this.#cardObserver?.disconnect();
    super.disconnectedCallback();
  }

  /** @returns {'search' | 'collection'} */
  get #mode() {
    return this.dataset.mode === 'collection' ? 'collection' : 'search';
  }

  /** ISO currency for price formatting (set from Liquid via data-currency). */
  get #currency() {
    return this.dataset.currency || 'EUR';
  }

  /** Whether Elevate's prices are in the presented currency — it indexes one price per product and never converts, so elsewhere they'd render EUR 39 as "SEK 39". */
  // Currency only: an adjusted shop-currency market would slip through, but every EUR catalog is at 0% and apc-voyado-member-price owns member/retail.
  get #trustVoyadoPrices() {
    // APC: Elevate indexes D2C prices; a B2B buyer's catalog price only comes from the product's `.js`.
    if (this.dataset.b2b === 'true') return false;
    const shopCurrency = this.dataset.shopCurrency;
    return !shopCurrency || shopCurrency === this.#currency;
  }

  /** Whether auto-load (infinite scroll) is on. */
  get #infinite() {
    return this.dataset.infiniteScroll !== 'false';
  }

  /** Manual pagination style when infinite scroll is off. */
  get #paginationStyle() {
    return this.dataset.paginationStyle === 'load_more' ? 'load_more' : 'numbered';
  }

  get #hasMore() {
    return this.#renderedCount < this.#totalHits;
  }

  /**
   * Promotional banners parsed from the server-rendered <template>s, sorted by their
   * 1-based grid position (a banner shows before the product at that position).
   * @returns {Array<{index: number, node: Element}>}
   */
  get #banners() {
    if (!this.#bannersParsed) {
      this.#bannersParsed = Array.from(this.querySelectorAll('template[data-voyado-banner]'))
        .map((template) => ({
          index: Number(/** @type {HTMLElement} */ (template).dataset.index),
          node: /** @type {HTMLTemplateElement} */ (template).content.firstElementChild,
        }))
        .filter((banner) => banner.node && Number.isFinite(banner.index) && banner.index > 0)
        .sort((a, b) => a.index - b.index)
        .map((banner) => ({ index: banner.index, node: /** @type {Element} */ (banner.node) }));
    }
    return this.#bannersParsed;
  }

  /** Highlights keyed by product handle (Voyado list products have no Shopify id). @returns {Map<string, {columns: string, rows: string, breakpoints: string}>} */
  get #highlights() {
    if (!this.#highlightsParsed) {
      this.#highlightsParsed = new Map();
      for (const template of this.querySelectorAll('template[data-voyado-highlight]')) {
        const data = /** @type {HTMLElement} */ (template).dataset;
        const handle = data.handle?.trim();
        if (!handle) continue;
        this.#highlightsParsed.set(handle, {
          columns: data.columns || 'span 1',
          rows: data.rows || 'span 1',
          breakpoints: data.breakpoints || 'all',
        });
      }
    }
    return this.#highlightsParsed;
  }

  /**
   * Turn a card's add-to-cart into a "choose your colours" call-to-action for a kit/bundle:
   * the button stays but relabels and, instead of quick-adding, the click routes to the PDP
   * (handled in #addToCart). Matches the client's live-site behaviour. No-op if already marked.
   * @param {ParentNode} root - the card (or its grid item) containing the ATC button
   */
  #markKitAddButton(root) {
    const button = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-voyado-slot="add-to-cart"]'));
    if (!button || button.dataset.kit === 'true') return;

    button.dataset.kit = 'true';
    button.disabled = false;
    const label = button.dataset.textChoose;
    if (!label) return;

    // Swap only the resting label; the "added" state never shows because kits don't quick-add.
    const text =
      button.querySelector('.add-to-cart-button-text span:last-child') ??
      button.querySelector('.add-to-cart-button-text');
    if (text) text.textContent = label;
    button.setAttribute('aria-label', label);
  }

  /** Shopify handle from a Voyado product link (`/products/{handle}`). @param {any} product @returns {string | null} */
  #productHandle(product) {
    if (!product?.link) return null;
    const match = new URL(product.link, location.origin).pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  async #start() {
    // Visual regression captures (apa-cli visual engine) wait for this to clear.
    this.setAttribute('data-visual-pending', '');
    this.#grid = this.querySelector('.product-grid');
    const template = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template[data-voyado-card]'));
    this.#card = template?.content.firstElementChild;
    if (!this.#grid || !this.#card) {
      this.removeAttribute('data-visual-pending');
      return;
    }

    // The Elevate app embed loads independently of this module, so `window.elevate` is often
    // absent on the first tick — checking once and bailing showed "no products found" for a
    // populated collection whenever the app lost the race (cold cache, slow connection).
    // Keep the skeletons up while we wait; only give up once it really doesn't arrive.
    const { signal } = this.#abortController;
    if (!isAvailable()) {
      const available = await waitForAvailable({ signal });
      if (signal.aborted) return;
      if (!available) {
        console.warn('[voyado] window.elevate never became available — is the Elevate app embed enabled?');
        this.#showEmpty();
        this.removeAttribute('data-visual-pending');
        return;
      }
    }

    this.#limit = Number(this.dataset.limit) || 24;

    // Numbered pagination: honour ?page= on initial load.
    let startPage = 1;
    if (!this.#infinite && this.#paginationStyle === 'numbered') {
      startPage = Math.max(1, Number(new URLSearchParams(location.search).get('page')) || 1);
    }
    this.#skip = (startPage - 1) * this.#limit;

    this.#facetsEl?.addEventListener('change', this.#onFacetChange, { signal });
    this.#sortEl?.addEventListener('change', this.#onSortChange, { signal });
    this.addEventListener('click', this.#onClearAll, { signal });
    this.#grid.addEventListener('click', this.#onCardClick, { signal });
    window.addEventListener('popstate', this.#onPopState, { signal });
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, { signal });

    await this.#loadPage({ replace: true });
  }

  /**
   * Fetch the page at the current #skip, forwarding facet filters + sort from the URL.
   * @returns {Promise<{ products: any[], totalHits: number, facets: any[], sort: any }>}
   */
  async #fetchPage() {
    /** @type {Record<string, any>} */
    const params = { limit: this.#limit, skip: this.#skip };

    // The URL is the source of truth for facet filters (f.*) and sort — forward them, normalising
    // range bounds (f.*.min/max) to dot-decimals so comma-decimal locales (nl-NL "9,99") parse.
    for (const [key, value] of new URLSearchParams(location.search)) {
      if (key === 'sort') {
        params[key] = value;
      } else if (key.startsWith('f.')) {
        if (/\.(min|max)$/.test(key)) {
          const normalized = this.#normalizeRangeValue(value);
          if (normalized) params[key] = normalized;
        } else {
          params[key] = value;
        }
      }
    }

    const empty = { products: [], totalHits: 0, facets: [], sort: null };
    let response;

    if (this.#mode === 'collection') {
      const pageReference = this.dataset.pageReference;
      if (!pageReference) return empty;
      params.pageReference = pageReference;
      response = await landingPage(params);
    } else {
      const query = new URLSearchParams(location.search).get('q')?.trim();
      if (!query) return empty;
      params.q = query;
      response = await searchPage(params);
      this.#applyAutocorrect(query, response);
    }

    const primaryList = /** @type {any} */ (response).primaryList;
    const list = normalizeList(primaryList);
    return { products: list.products, totalHits: list.totalHits, facets: list.facets, sort: primaryList?.sort };
  }

  /**
   * Persist Elevate's autocorrected phrase to the URL so facet/sort/page requests keep it, and show the note.
   * @param {string} query - The search phrase sent to Elevate
   * @param {any} response - The raw searchPage response
   */
  #applyAutocorrect(query, response) {
    const corrected = response?.autoCorrect?.q;
    if (!corrected || corrected === query) return;

    const url = new URL(location.href);
    url.searchParams.set('q', corrected);
    history.replaceState(history.state, '', url);

    // Note lives in the search-header section (a sibling section), hence the document-level query.
    const note = /** @type {HTMLElement | null} */ (document.querySelector('[data-voyado-autocorrect]'));
    if (note?.dataset.template) {
      note.textContent = note.dataset.template.replace('[[term]]', corrected);
      note.hidden = false;
    }
  }

  /**
   * Load the current page and render it, then (re)render pagination controls.
   * @param {{ replace: boolean, attempt?: number }} options - replace the grid (first/numbered) or
   *   append (infinite/load-more); `attempt` tracks the one retry a failed full load gets
   */
  async #loadPage({ replace, attempt = 0 }) {
    if (this.#loading || !this.#grid || !this.#card) return;
    this.#loading = true;
    this.setAttribute('data-visual-pending', '');

    try {
      const { products, totalHits, facets, sort } = await this.#fetchPage();
      if (this.#abortController.signal.aborted) return;
      this.#totalHits = totalHits;

      // Facets/sort/count only change on a full reload (first load / filter / sort change).
      if (replace) {
        this.#renderCount(totalHits);
        this.#renderFacets(facets);
        this.#renderSort(sort);
      }

      if (replace && !products.length) {
        this.#showEmpty();
        return;
      }

      const fragment = document.createDocumentFragment();
      products.forEach((product, i) => {
        const index = replace ? i : this.#renderedCount + i;
        // Insert any banner positioned before this product (1-based grid position).
        const position = index + 1;
        for (const banner of this.#banners) {
          if (banner.index === position) fragment.appendChild(banner.node.cloneNode(true));
        }
        fragment.appendChild(this.#buildItem(this.#card, product, index));
      });

      this.#clearControls();
      if (replace) {
        this.#grid.replaceChildren(fragment);
        this.#renderedCount = products.length;
      } else {
        this.#grid.appendChild(fragment);
        this.#renderedCount += products.length;
      }

      this.#renderControls();
      this.#observeCards();
    } catch (error) {
      console.error('[voyado] grid render failed', error);
      // A single transient failure (network blip, or Elevate answering before its context is
      // warm) would otherwise read to the shopper as "no products found" on a populated
      // collection — retry the full load once before falling back to the empty state.
      if (replace && attempt === 0 && !this.#abortController.signal.aborted) {
        this.#loading = false;
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        if (this.#abortController.signal.aborted) return;
        return this.#loadPage({ replace, attempt: attempt + 1 });
      }
      if (replace) this.#showEmpty();
    } finally {
      this.#loading = false;
      this.removeAttribute('data-visual-pending');
    }
  }

  /** Remove any sentinel / pagination controls from a previous render. */
  #clearControls() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#cardObserver?.disconnect();
    this.#cardObserver = null;
    this.#grid?.querySelector('[data-voyado-sentinel]')?.remove();
    this.#grid?.parentElement?.querySelector('[data-voyado-pagination]')?.remove();
    this.#grid?.parentElement?.querySelector('[data-voyado-empty]')?.remove();
  }

  /** Render the appropriate pagination control for the current mode. */
  #renderControls() {
    if (this.#infinite) {
      if (this.#hasMore) this.#addSentinel();
    } else if (this.#paginationStyle === 'load_more') {
      this.#renderLoadMore();
    } else {
      this.#renderNumbered();
    }
  }

  /** Infinite scroll: observe a sentinel and append the next page when it nears the viewport. */
  #addSentinel() {
    if (!this.#grid) return;
    const sentinel = document.createElement('li');
    sentinel.dataset.voyadoSentinel = '';
    sentinel.setAttribute('aria-hidden', 'true');
    this.#grid.appendChild(sentinel);

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !this.#loading) {
          this.#skip += this.#limit;
          this.#loadPage({ replace: false });
        }
      },
      { rootMargin: '200px' },
    );
    this.#observer.observe(sentinel);
  }

  /** Load-more button (appends the next page on click). */
  #renderLoadMore() {
    if (!this.#hasMore || !this.#grid?.parentElement) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'load-more';
    wrapper.dataset.voyadoPagination = '';

    const button = document.createElement('button');
    button.type = 'button';
    const style = this.dataset.loadMoreStyle === 'primary' ? 'primary' : 'secondary';
    button.className = `load-more__button button button-m button-${style}`;
    button.textContent = this.dataset.loadMoreText || 'Load more';
    button.addEventListener(
      'click',
      () => {
        this.#skip += this.#limit;
        this.#loadPage({ replace: false });
      },
      { signal: this.#abortController.signal },
    );
    wrapper.appendChild(button);

    if (this.dataset.showRemaining === 'true') {
      const remaining = Math.max(0, this.#totalHits - this.#renderedCount);
      const count = document.createElement('p');
      count.className = 'load-more__count';
      count.textContent = `${remaining} ${this.dataset.remainingText || ''}`.trim();
      wrapper.appendChild(count);
    }

    this.#grid.parentElement.appendChild(wrapper);
  }

  /** Numbered pagination (replaces the grid with the chosen page). */
  #renderNumbered() {
    const pages = Math.ceil(this.#totalHits / this.#limit);
    if (pages <= 1 || !this.#grid?.parentElement) return;

    const current = Math.floor(this.#skip / this.#limit) + 1;

    const nav = document.createElement('nav');
    nav.className = 'pagination';
    nav.dataset.voyadoPagination = '';

    const list = document.createElement('ul');
    list.className = 'pagination__list';
    list.setAttribute('role', 'list');

    /** @param {number} page @param {string} label @param {boolean} [isCurrent] */
    const addPage = (page, label, isCurrent) => {
      const item = document.createElement('li');
      item.className = 'pagination__item';
      const link = document.createElement('a');
      link.className = `pagination__link${isCurrent ? ' pagination__link--current' : ''}`;
      link.textContent = label;
      const url = new URL(location.href);
      url.searchParams.set('page', String(page));
      link.href = url.toString();
      if (isCurrent) link.setAttribute('aria-current', 'page');
      link.addEventListener('click', (event) => this.#onNumberedClick(event, page), {
        signal: this.#abortController.signal,
      });
      item.appendChild(link);
      list.appendChild(item);
    };

    // Windowed page set: 1 … current-1 current current+1 … last
    const windowPages = new Set([1, pages, current, current - 1, current + 1]);
    let previous = 0;
    for (let page = 1; page <= pages; page++) {
      if (!windowPages.has(page)) continue;
      if (page - previous > 1) {
        const gap = document.createElement('li');
        gap.className = 'pagination__item';
        gap.innerHTML = '<span class="pagination__link pagination__link--gap" aria-hidden="true">…</span>';
        list.appendChild(gap);
      }
      addPage(page, String(page), page === current);
      previous = page;
    }

    nav.appendChild(list);
    this.#grid.parentElement.appendChild(nav);
  }

  /**
   * @param {MouseEvent} event
   * @param {number} page
   */
  #onNumberedClick(event, page) {
    event.preventDefault();
    const url = new URL(location.href);
    url.searchParams.set('page', String(page));
    history.pushState({}, '', url.toString());
    this.#skip = (page - 1) * this.#limit;
    this.#loadPage({ replace: true }).then(() => {
      this.#grid?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  get #facetsEl() {
    return this.querySelector('[data-voyado-facets]');
  }

  get #sortEl() {
    return this.querySelector('[data-voyado-sort]');
  }

  /** Set the product count in the controls bar + the drawer footer. @param {number} totalHits */
  #renderCount(totalHits) {
    const el = /** @type {HTMLElement | null} */ (this.querySelector('[data-voyado-count]'));
    if (el) {
      const template = el.dataset.countTemplate;
      el.textContent = template ? template.replace('[[count]]', String(totalHits)) : String(totalHits);
    }

    // Drawer "See N items" button.
    const seeResults = /** @type {HTMLElement | null} */ (this.querySelector('[data-voyado-see-results]'));
    if (seeResults) {
      const template = seeResults.dataset.seeResultsTemplate;
      seeResults.textContent = template ? template.replace('[[count]]', String(totalHits)) : String(totalHits);
    }

    // "Clear all" is only shown when at least one facet filter is active.
    const hasFilters = Array.from(new URLSearchParams(location.search).keys()).some((key) => key.startsWith('f.'));
    this.querySelector('[data-voyado-clear-all]')?.classList.toggle('active', hasFilters);
  }

  /**
   * Render sort options into the native floating-panel by cloning the option <template>.
   * @param {any} sort
   */
  #renderSort(sort) {
    const container = this.#sortEl;
    const optionsSlot = container?.querySelector('[data-voyado-slot="options"]');
    const optionTemplate = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector('template[data-voyado-sort-option-template]')
    );
    if (!container || !optionsSlot || !optionTemplate?.content.firstElementChild || !sort?.options?.length) return;

    optionsSlot.replaceChildren();
    let selectedLabel = '';

    for (const option of sort.options) {
      const item = /** @type {HTMLElement} */ (optionTemplate.content.firstElementChild.cloneNode(true));
      const input = /** @type {HTMLInputElement | null} */ (item.querySelector('[data-voyado-slot="input"]'));
      if (input) {
        input.value = option.id;
        input.checked = option.id === sort.selected;
      }
      const label = item.querySelector('[data-voyado-slot="label"]');
      if (label) label.textContent = option.label;
      const optionRow = item.querySelector('.sorting-filter__option');
      optionRow?.setAttribute('aria-selected', option.id === sort.selected ? 'true' : 'false');
      if (option.id === sort.selected) selectedLabel = option.label;
      optionsSlot.appendChild(item);
    }

    const status = container.querySelector('[data-voyado-slot="status"]');
    if (status) status.textContent = selectedLabel;
  }

  /**
   * Render the facet groups by cloning the Liquid <template>s and filling slots.
   * @param {any[]} facets
   */
  #renderFacets(facets) {
    const container = this.#facetsEl;
    if (!container) return;
    if (!facets?.length) {
      container.replaceChildren();
      return;
    }

    const groupTemplate = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector('template[data-voyado-facet-group-template]')
    );
    const optionTemplate = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector('template[data-voyado-facet-option-template]')
    );
    const rangeTemplate = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector('template[data-voyado-facet-range-template]')
    );
    const swatchTemplate = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector('template[data-voyado-facet-swatch-template]')
    );
    if (!groupTemplate?.content.firstElementChild) return;

    const urlParams = new URLSearchParams(location.search);
    const fragment = document.createDocumentFragment();

    for (const facet of facets) {
      const group = /** @type {HTMLElement} */ (groupTemplate.content.firstElementChild.cloneNode(true));
      const label = group.querySelector('[data-voyado-slot="label"]');
      if (label) label.textContent = facet.label;
      const values = group.querySelector('[data-voyado-slot="values"]');
      if (!values) continue;

      let activeCount = 0;

      if (facet.type === 'RANGE' && rangeTemplate?.content.firstElementChild) {
        const range = /** @type {HTMLElement} */ (rangeTemplate.content.firstElementChild.cloneNode(true));
        const min = /** @type {HTMLInputElement | null} */ (range.querySelector('[data-voyado-slot="min"]'));
        const max = /** @type {HTMLInputElement | null} */ (range.querySelector('[data-voyado-slot="max"]'));
        const minLabel = /** @type {HTMLLabelElement | null} */ (range.querySelector('[data-voyado-slot="min-label"]'));
        const maxLabel = /** @type {HTMLLabelElement | null} */ (range.querySelector('[data-voyado-slot="max-label"]'));
        const minValue = urlParams.get(`f.${facet.id}.min`) || '';
        const maxValue = urlParams.get(`f.${facet.id}.max`) || '';
        if (min) {
          min.id = `voyado-facet-${facet.id}-min`;
          min.placeholder = facet.min != null ? String(facet.min) : '';
          min.value = minValue;
          min.dataset.facetId = facet.id;
          min.dataset.bound = 'min';
          if (minLabel) minLabel.htmlFor = min.id;
        }
        if (max) {
          max.id = `voyado-facet-${facet.id}-max`;
          max.placeholder = facet.max != null ? String(facet.max) : '';
          max.value = maxValue;
          max.dataset.facetId = facet.id;
          max.dataset.bound = 'max';
          if (maxLabel) maxLabel.htmlFor = max.id;
        }
        if (minValue || maxValue) activeCount = 1;
        // The range markup is a <price-facet> block, not <li>s — replace the <ul> with it
        // so the structure is `.facets__inputs-wrapper > .price-facet` (valid; native parity).
        values.replaceWith(range);
      } else {
        const isSwatch = this.#isColorFacet(facet) && Boolean(swatchTemplate?.content.firstElementChild);
        const template = isSwatch ? swatchTemplate : optionTemplate;
        if (template?.content.firstElementChild) {
          const selected = (urlParams.get(`f.${facet.id}`) || '').split('|').filter(Boolean);
          let valueIndex = 0;
          for (const value of facet.values || []) {
            const option = /** @type {HTMLElement} */ (template.content.firstElementChild.cloneNode(true));
            const input = /** @type {HTMLInputElement | null} */ (option.querySelector('[data-voyado-slot="input"]'));
            // Unique id so the global checkbox/swatch `:checked` styling and the label's
            // `for` association both work (native markup requires it).
            const inputId = `voyado-facet-${facet.id}-${valueIndex++}`;
            if (input) {
              input.id = inputId;
              input.value = value.id;
              input.dataset.facetId = facet.id;
              if (isSwatch) input.setAttribute('aria-label', value.label || '');
              input.checked = Boolean(value.selected) || selected.includes(String(value.id));
              if (input.checked) activeCount += 1;
            }
            const labelFor = /** @type {HTMLLabelElement | null} */ (
              option.querySelector('[data-voyado-slot="label-for"]')
            );
            if (labelFor) labelFor.htmlFor = inputId;
            const optionLabel = option.querySelector('[data-voyado-slot="label"]');
            if (optionLabel) optionLabel.textContent = value.label;

            // APC: per-value product count from Elevate (value.count).
            const apcCountLabel = value.count == null ? '' : `(${value.count})`;
            const apcOptionCount = option.querySelector('[data-apc-facet-count]');
            if (apcOptionCount) apcOptionCount.textContent = apcCountLabel;

            if (isSwatch) {
              option.querySelector('[data-voyado-slot="fieldset"]')?.setAttribute('aria-label', value.label || '');
              const swatch = /** @type {HTMLElement | null} */ (option.querySelector('[data-voyado-slot="swatch"]'));
              if (swatch) {
                const color = this.#swatchColor(value);
                if (color) swatch.style.setProperty('--swatch-background', color);
                else swatch.classList.add('swatch--unavailable');
              }
            }
            values.appendChild(option);
          }

          if (isSwatch) {
            values.classList.add('facets__inputs-list--swatches');
            /** @type {HTMLElement} */ (values).style.setProperty('--swatch-columns', '4');
            values.parentElement?.classList.add('facets__inputs-wrapper--row');
          }
        }
      }

      const status = group.querySelector('[data-voyado-slot="status"]');
      if (status) status.textContent = activeCount ? `(${activeCount})` : '';
      fragment.appendChild(group);
    }

    container.replaceChildren(fragment);
  }

  /**
   * Whether a facet should render as colour swatches rather than checkboxes.
   * @param {any} facet
   */
  #isColorFacet(facet) {
    return facet?.type === 'COLOR' || /colou?r/i.test(String(facet?.id || ''));
  }

  /**
   * Resolve a CSS colour for a swatch. Voyado may carry an explicit colour; otherwise
   * derive a CSS colour keyword from the value id/label (e.g. DARK_BLUE → darkblue).
   * Returns null when nothing resolves, so the swatch renders as `swatch--unavailable`.
   * @param {any} value
   * @returns {string | null}
   */
  #swatchColor(value) {
    const candidates = [value.color, value.hex, value.swatch?.color, value.id, value.label];
    for (const candidate of candidates) {
      if (!candidate) continue;
      const keyword = String(candidate)
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, '');
      if (this.#isCssColor(candidate)) return String(candidate);
      if (this.#isCssColor(keyword)) return keyword;
    }
    return null;
  }

  /**
   * @param {string} value
   * @returns {boolean}
   */
  #isCssColor(value) {
    const probe = new Option().style;
    probe.color = '';
    probe.color = String(value);
    return probe.color !== '';
  }

  /**
   * Apply a facet checkbox / range change: rewrite the URL and reload from page 1.
   * @param {Event} event
   */
  #onFacetChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.dataset.facetId) return;

    const url = new URL(location.href);
    url.searchParams.delete('page');

    if (target.type === 'checkbox') {
      const facetId = target.dataset.facetId;
      const checked = Array.from(
        this.#facetsEl?.querySelectorAll(`input[type="checkbox"][data-facet-id="${CSS.escape(facetId)}"]:checked`) ??
          [],
      ).map((input) => /** @type {HTMLInputElement} */ (input).value);
      if (checked.length) url.searchParams.set(`f.${facetId}`, checked.join('|'));
      else url.searchParams.delete(`f.${facetId}`);
    } else if (target.dataset.bound) {
      const key = `f.${target.dataset.facetId}.${target.dataset.bound}`;
      // Normalise to a dot-decimal so comma-decimal locales (nl-NL "9,99") don't break the Elevate query.
      const value = this.#normalizeRangeValue(target.value);
      if (value) url.searchParams.set(key, value);
      else url.searchParams.delete(key);
    }

    this.#navigate(url);
  };

  /**
   * Apply a sort change.
   * @param {Event} event
   */
  #onSortChange = (event) => {
    const target = /** @type {HTMLInputElement} */ (event.target);
    if (!target?.value) return;
    this.#sortEl?.querySelector('details[open]')?.removeAttribute('open'); // close the panel
    const url = new URL(location.href);
    url.searchParams.delete('page');
    url.searchParams.set('sort', target.value);
    this.#navigate(url);
  };

  /** "Clear all" in the drawer footer: drop every facet filter and reload from page 1. */
  #onClearAll = (/** @type {Event} */ event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-voyado-clear-all]') : null;
    if (!target) return;
    event.preventDefault();
    const url = new URL(location.href);
    for (const key of Array.from(url.searchParams.keys())) {
      if (key.startsWith('f.') || key === 'page') url.searchParams.delete(key);
    }
    this.#navigate(url);
  };

  /**
   * Lazily enrich cards from the Shopify product (.js) when they scroll into view, with one
   * cached fetch per product. Voyado's list data lacks the Shopify product id and a second
   * image, so this fills the native <wishlist-button> id and the secondary hover image.
   */
  #observeCards() {
    // Converted market: fill every card in the batch now — waiting for each to scroll into view is what delayed the price.
    if (!this.#trustVoyadoPrices) {
      for (const item of this.#grid?.querySelectorAll('.product-grid__item[data-voyado-link]') ?? []) {
        const link = /** @type {HTMLElement} */ (item).dataset.voyadoLink;
        if (link) this.#injectPrice(/** @type {HTMLElement} */ (item), this.#resolveProduct(link));
      }
    }

    const items = this.#grid?.querySelectorAll(
      '.product-grid__item:has(wishlist-button[data-voyado-slot="wishlist"]:not([data-product-id])), ' +
        '.product-grid__item:has([data-voyado-slot="image-secondary"][hidden]), ' +
        '.product-grid__item:has([data-voyado-slot="badges"]:empty), ' +
        // Price left empty by #fillPrice on a converted market — the product's `.js` fills it (#injectPrice).
        '.product-grid__item:has([data-voyado-slot="price"]:empty), ' +
        // Also enrich cards that still have an ATC button, so kits/bundles can drop it (see #enrichCard).
        '.product-grid__item:has([data-voyado-slot="add-to-cart"]), ' +
        // ...and any card with a sold-out badge, so Shopify's online availability can set it.
        '.product-grid__item:has([data-voyado-slot="badge-soldout"])',
    );
    if (!items?.length) return;

    if (!this.#cardObserver) {
      this.#cardObserver = new IntersectionObserver(
        (entries, observer) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            observer.unobserve(entry.target);
            this.#enrichCard(/** @type {HTMLElement} */ (entry.target));
          }
        },
        { rootMargin: '200px' },
      );
    }

    items.forEach((item) => this.#cardObserver?.observe(item));
  }

  /**
   * Resolve a product's Shopify `.js`, then set the wishlist id + secondary hover image.
   * @param {HTMLElement} item
   */
  async #enrichCard(item) {
    const link = item.dataset.voyadoLink;
    if (!link) return;

    // Never await the fragment before this: the .js carries the market price and is ~2.5x faster, so serialising them delayed every price.
    const pending = this.#resolveProduct(link);
    this.#injectPrice(item, pending);

    // The observer watches each card once, so a failed fragment would leave it permanently without
    // badges and with a kit's quick-add intact. cardData drops failures, so retry once.
    if (!(await cardData(link)).ok) await cardData(link);

    // Badges + kit flag come from the server-rendered voyado-badges fragment (Shopify metafields
    // aren't in the .js). One cached fetch feeds both #injectBadges and the kit relabel below.
    this.#injectBadges(item, link);

    // Kits/bundles must not be quick-added from the grid — relabel the ATC to "choose your
    // colours" so the click routes to the PDP, using the same apa_kit_size signal as the native
    // card (kit-variants-check), surfaced via the fragment.
    const { isKit } = await cardData(link);
    const product = await pending;

    // Online availability is Shopify's `available` (the fulfilment location only, as on the PDP),
    // not Voyado's inStock, which also counts boutique stock. Sold out wins over the kit relabel.
    if (product) this.#setAvailability(item, product.available !== false);
    if (isKit && product?.available !== false) this.#markKitAddButton(item);

    if (!product) return;

    const wishlist = item.querySelector('wishlist-button[data-voyado-slot="wishlist"]:not([data-product-id])');
    if (wishlist && product.id != null) wishlist.setAttribute('data-product-id', String(product.id));

    const secondary = /** @type {HTMLImageElement | null} */ (
      item.querySelector('[data-voyado-slot="image-secondary"][hidden]')
    );
    if (secondary) {
      const primary = /** @type {HTMLImageElement | null} */ (item.querySelector('[data-voyado-slot="image"]'));
      const primaryKey = primary?.src ? new URL(primary.src, location.origin).pathname : null;
      const hoverImage = (product.images || []).find((url) => new URL(url, location.origin).pathname !== primaryKey);
      if (hoverImage) {
        const src = new URL(hoverImage, location.origin);
        src.searchParams.set('width', '500');
        secondary.src = src.toString();
        secondary.alt = product.title || '';
        secondary.hidden = false;
      } else {
        secondary.remove();
      }
    }
  }

  /**
   * Fetch + cache a product's `.js` payload, keyed by path.
   * @param {string} link
   * @returns {Promise<any|null>}
   */
  #resolveProduct(link) {
    const path = new URL(link, location.origin).pathname;
    let pending = this.#productCache.get(path);
    if (!pending) {
      pending = fetch(`${path}.js`)
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);
      this.#productCache.set(path, pending);
    }
    return pending;
  }

  /**
   * Route product-card interactions: add to cart, then card-link click notification.
   * The wishlist heart is the native <wishlist-button> component and handles its own click.
   */
  #onCardClick = (/** @type {Event} */ event) => {
    const el = event.target instanceof Element ? event.target : null;
    if (!el) return;
    if (el.closest('[data-voyado-slot="wishlist"]')) return; // native component owns this

    const add = /** @type {HTMLButtonElement | null} */ (el.closest('[data-voyado-slot="add-to-cart"]'));
    if (add) {
      event.preventDefault();
      this.#addToCart(add);
      return;
    }

    // Following the card: fire a Voyado click notification (fire-and-forget).
    if (el.closest('[data-voyado-slot="link"]')) {
      const item = /** @type {HTMLElement | null} */ (el.closest('.product-grid__item'));
      if (item?.dataset.voyadoTicket) notify('click', item.dataset.voyadoTicket);
    }
  };

  /**
   * Add a Voyado card's product to the cart. Voyado data carries no Shopify variant id,
   * so resolve it from the product's `.js`: single available variant → add directly and
   * fire the add-to-cart notification; otherwise send the shopper to the PDP to choose.
   * @param {HTMLButtonElement} button
   */
  async #addToCart(button) {
    if (button.disabled) return;
    const item = /** @type {HTMLElement | null} */ (button.closest('.product-grid__item'));
    const link = item?.dataset.voyadoLink;
    if (!link) return;

    button.disabled = true;
    try {
      // Kit/bundle → PDP. Prefer the marker set during enrichment; otherwise consult the
      // server-rendered fragment (same apa_kit_size signal as the native card).
      const isKit = button.dataset.kit === 'true' || (await cardData(link)).isKit;

      const product = await this.#resolveProduct(link);
      const available = (product?.variants || []).filter((/** @type {any} */ v) => v.available);

      // Kits/bundles (colours chosen on the PDP), multiple choices, or none resolvable →
      // let the shopper pick on the PDP instead of quick-adding.
      if (isKit || product?.variants?.length !== 1 || !available.length) {
        location.href = link;
        return;
      }

      const variantId = available[0].id;
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      if (!response.ok) throw new Error(`cart add ${response.status}`);

      // Let the cart drawer/icon update (it re-fetches its section off this event).
      this.dispatchEvent(
        new CartAddEvent({}, String(variantId), { source: 'voyado-grid', itemCount: 1, productId: product.id }),
      );

      if (item?.dataset.voyadoTicket) notify('addToCart', item.dataset.voyadoTicket);

      // Trigger the native added-state animation (check + "Added").
      button.setAttribute('data-added', '');
      setTimeout(() => button.removeAttribute('data-added'), 2000);
    } catch (error) {
      console.error('[voyado] add to cart failed', error);
      location.href = link; // fall back to the PDP
    } finally {
      button.disabled = false;
    }
  }

  #onPopState = () => {
    this.#skip = 0;
    this.#loadPage({ replace: true });
  };

  /** Close any open control popover when clicking/tapping outside it (native facets behavior). */
  #onDocumentPointerDown = (/** @type {Event} */ event) => {
    const openDetails = this.querySelectorAll('.voyado-controls details[open]');
    openDetails.forEach((details) => {
      if (!details.contains(/** @type {Node} */ (event.target))) details.removeAttribute('open');
    });
  };

  /**
   * Push a new URL and reload the grid from the first page.
   * @param {URL} url
   */
  #navigate(url) {
    history.pushState({}, '', url.toString());
    this.#skip = 0;
    this.#loadPage({ replace: true });
  }

  /** Replace the skeletons with a single empty-state message. */
  #showEmpty() {
    if (!this.#grid) return;
    this.#clearControls();
    this.#grid.replaceChildren();
    this.#renderedCount = 0;

    const wrapper = this.#grid.parentElement;
    wrapper?.querySelector('[data-voyado-empty]')?.remove();

    // Prefer the server-rendered empty template (native .main-collection-grid__empty markup,
    // with translated copy + clear-filters link); fall back to the plain message.
    const template = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector('template[data-voyado-empty-template]')
    );
    let empty;
    if (template?.content.firstElementChild) {
      empty = /** @type {HTMLElement} */ (template.content.firstElementChild.cloneNode(true));
    } else if (this.dataset.emptyText) {
      empty = document.createElement('div');
      empty.className = 'voyado-grid__empty';
      empty.textContent = this.dataset.emptyText;
    }
    if (empty) {
      empty.setAttribute('data-voyado-empty', '');
      // Sibling of the grid <ul> (the empty markup is a <div>, not a list item).
      (wrapper || this.#grid).appendChild(empty);
    }
  }

  /**
   * Build a grid <li> wrapping a filled clone of the Voyado card template.
   * @param {any} card - The template's card element
   * @param {any} product
   * @param {number} index
   * @returns {HTMLLIElement}
   */
  #buildItem(card, product, index) {
    const item = document.createElement('li');
    item.className = 'product-grid__item';
    item.dataset.productId = product.key || '';
    // Ticket drives Voyado click/add-to-cart notifications; key drives favorites; link
    // resolves the Shopify variant for add-to-cart (Voyado data has no Shopify variant id).
    // Voyado links come back unlocalized — re-prefix so PDP navigation and the fragment/.js
    // fetches stay in the shopper's locale + market.
    if (product.ticket) item.dataset.voyadoTicket = product.ticket;
    if (product.key) item.dataset.voyadoKey = product.key;
    if (product.link) item.dataset.voyadoLink = localizeUrl(product.link);

    // Enlarge this cell when the merchant flagged the product as a highlight (matched by handle).
    if (this.#highlights.size) {
      const handle = this.#productHandle(product);
      const highlight = handle ? this.#highlights.get(handle) : null;
      if (highlight) {
        item.classList.add('product-grid__item--highlight', `product-grid__item--highlight-${highlight.breakpoints}`);
        item.style.setProperty('--highlight-columns', highlight.columns);
        item.style.setProperty('--highlight-rows', highlight.rows);
      }
    }

    const clone = /** @type {HTMLElement} */ (card.cloneNode(true));
    this.#fill(clone, product, index);
    item.appendChild(clone);
    return item;
  }

  /**
   * Fill a cloned card's data slots from a Voyado product.
   * @param {HTMLElement} card
   * @param {any} product
   * @param {number} index
   */
  #fill(card, product, index) {
    const title = product.title || product.name || '';
    const url = product.link ? localizeUrl(product.link) : '#';

    card.querySelectorAll('[data-voyado-slot="link"]').forEach((anchor) => anchor.setAttribute('href', url));

    const srLabel = card.querySelector('[data-voyado-slot="title-sr"]');
    if (srLabel) srLabel.textContent = title;

    const titleNode = card.querySelector('[data-voyado-slot="title"]');
    if (titleNode) titleNode.textContent = title;

    const image = /** @type {HTMLImageElement | null} */ (card.querySelector('[data-voyado-slot="image"]'));
    const source = product.imageInfo?.images?.[0]?.sources?.[0];
    if (image && source?.url) {
      image.src = source.url;
      image.alt = title;
      if (source.width) image.width = source.width;
      if (source.height) image.height = source.height;
      image.setAttribute('loading', index < 4 ? 'eager' : 'lazy');
    }

    // Secondary image (hover cross-fade). Voyado's list response usually carries only the
    // primary image, so prefer images[1] when present, else leave the slot hidden for the
    // in-view enrichment pass to fill from the Shopify product (.js).
    const secondary = /** @type {HTMLImageElement | null} */ (
      card.querySelector('[data-voyado-slot="image-secondary"]')
    );
    const primaryKey = source?.url ? new URL(source.url, location.origin).pathname : null;
    const secondarySource = (product.imageInfo?.images || [])
      .map((img) => img?.sources?.[0])
      .find((s) => s?.url && new URL(s.url, location.origin).pathname !== primaryKey);
    if (secondary && secondarySource?.url) {
      secondary.src = secondarySource.url;
      secondary.alt = title;
      if (secondarySource.width) secondary.width = secondarySource.width;
      if (secondarySource.height) secondary.height = secondarySource.height;
      secondary.hidden = false;
    }

    this.#fillPrice(card, product);

    // First guess from Voyado's inStock, which counts every location (boutiques included). The
    // in-view enrichment pass corrects it from Shopify's online `available` (see #enrichCard).
    // (Kit/bundle relabeling also happens there, from the apa_kit_size flag in the fragment.)
    this.#setAvailability(card, product.inStock !== false);
  }

  /**
   * Inject a product's `product_badge` metaobject badges into its card's badge slot.
   * The slot lives in the card's absolute badges container (alongside the sold-out badge);
   * an empty slot is removed so the container collapses when there are no badges.
   * @param {HTMLElement} item
   * @param {string} link
   */
  async #injectBadges(item, link) {
    const slot = item.querySelector('[data-voyado-slot="badges"]:empty');
    if (!slot) return;

    const { badges } = await cardData(link);
    if (this.#abortController.signal.aborted || !slot.isConnected) return;

    if (!badges.length) {
      slot.remove();
      return;
    }
    slot.innerHTML = badges.join('');
  }

  /**
   * Fill a card's price slots from the product's `.js` (cents, presented currency), unless #fillPrice already filled them.
   * @param {HTMLElement} item
   * @param {Promise<any|null>} pending - the product's `.js`, in flight
   */
  async #injectPrice(item, pending) {
    const priceNode = item.querySelector('[data-voyado-slot="price"]:empty');
    if (!priceNode) return;

    const product = await pending;
    // Still empty: apc-voyado-member-price may have filled it during the await, and its amount must win.
    if (this.#abortController.signal.aborted || !priceNode.isConnected || priceNode.textContent) return;
    if (product?.price_min == null) return;

    // A varying product shows price_min behind a "From" label, like price.liquid's card semantics.
    const amount = this.#formatMoney(product.price_min / 100);
    const from = product.price_varies ? this.dataset.priceFrom : null;
    priceNode.textContent = from ? from.replace('@price@', amount) : amount;

    const compareNode = item.querySelector('[data-voyado-slot="compare"]');
    if (!compareNode) return;
    const compare = product.compare_at_price_min;
    if (compare && compare > product.price_min) {
      compareNode.textContent = this.#formatMoney(compare / 100);
      compareNode.removeAttribute('hidden');
    } else {
      compareNode.remove();
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {any} product
   */
  #fillPrice(card, product) {
    // Converted market: leave the slots empty for #injectPrice to fill from the product's `.js`.
    if (!this.#trustVoyadoPrices) return;

    const priceNode = card.querySelector('[data-voyado-slot="price"]');
    const compareNode = card.querySelector('[data-voyado-slot="compare"]');

    // Voyado prices are major currency units (e.g. 12.99).
    const selling = product.sellingPrice?.min;
    const list = product.listPrice?.min;

    if (priceNode && selling != null) priceNode.textContent = this.#formatMoney(selling);

    if (compareNode) {
      if (list != null && selling != null && list > selling) {
        compareNode.textContent = this.#formatMoney(list);
        compareNode.removeAttribute('hidden');
      } else {
        compareNode.remove();
      }
    }
  }

  /**
   * Toggle a card's sold-out badge and add-to-cart. Reversible, so Shopify's availability can
   * overrule Voyado's first guess either way.
   * @param {ParentNode} root - the card (or its grid item)
   * @param {boolean} available
   */
  #setAvailability(root, available) {
    root.querySelector('[data-voyado-slot="badge-soldout"]')?.toggleAttribute('hidden', available);

    const button = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-voyado-slot="add-to-cart"]'));
    if (!button || available === (button.dataset.soldOut !== 'true')) return;

    // Swap only the resting label (same node #markKitAddButton relabels), keeping the "added" markup.
    const text =
      button.querySelector('.add-to-cart-button-text span:last-child') ??
      button.querySelector('.add-to-cart-button-text');

    if (available) {
      delete button.dataset.soldOut;
      button.disabled = false;
      if (text && button.dataset.textResting) text.textContent = button.dataset.textResting;
    } else {
      button.dataset.soldOut = 'true';
      button.disabled = true;
      if (text) {
        button.dataset.textResting = text.textContent ?? '';
        if (button.dataset.textSoldOut) text.textContent = button.dataset.textSoldOut;
      }
    }
  }

  /**
   * Normalise a localized numeric range value (e.g. nl-NL "9,99" or "1.234,50") to a dot-decimal
   * string Elevate can parse as a double. The last separator is treated as the decimal mark; any
   * earlier separators are dropped as thousands separators. Returns '' when empty/unparseable.
   * @param {string} raw
   * @returns {string}
   */
  #normalizeRangeValue(raw) {
    let value = String(raw)
      .trim()
      .replace(/[^0-9.,]/g, '');
    if (!value) return '';
    const decimal = Math.max(value.lastIndexOf(','), value.lastIndexOf('.'));
    if (decimal !== -1) {
      value = value.slice(0, decimal).replace(/[.,]/g, '') + '.' + value.slice(decimal + 1).replace(/[.,]/g, '');
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? String(parsed) : '';
  }

  /**
   * @param {number} amount
   * @returns {string}
   */
  #formatMoney(amount) {
    // Prefer the market's own money format (data-money-sample); Intl is the fallback.
    const sample = this.dataset.moneySample;
    if (sample) {
      this.#money ??= moneyFormatter(sample);
      return this.#money(amount);
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: this.#currency,
      }).format(amount);
    } catch {
      return String(amount);
    }
  }
}

if (!customElements.get('voyado-grid')) {
  customElements.define('voyado-grid', VoyadoGrid);
}
